// Lightweight internal specialist router.
//
// Pure, deterministic, zero-latency: no extra model call, so routing costs
// nothing and is unit-testable. The user never sees these role names as
// "personalities" — it is one intelligence with different depth settings.

import type { SpecialistRole } from "./constitution";

export interface RouteDecision {
  role: SpecialistRole;
  reason: string;
  /** True when the user explicitly forced the role. */
  forced: boolean;
}

const SIGNALS: { role: SpecialistRole; weight: number; re: RegExp }[] = [
  // DEEP TEMPLE — hard, multi-tradition, existential, or explicitly deep.
  { role: "deep_temple", weight: 5, re: /\b(deep temple|go deep|think deeply|hardest|paradox|reconcile|contradiction|meaning of life|why do we suffer|nature of (self|reality|mind)|non-?duality|emptiness and form)\b/i },
  { role: "deep_temple", weight: 3, re: /\b(compare|versus|vs\.?)\b.*\b(buddhis[mt]|kemetic|vedic|taois[mt]|christian|islam|yoruba|hermetic)\b/i },

  // SCHOLAR — knowledge, history, doctrine, definitions.
  { role: "scholar", weight: 3, re: /\b(what does .* mean|define|definition|history|historical|origin|scripture|sutra|sutta|pali|sanskrit|neter|ma'?at|dharma|karma|doctrine|tradition|scholar|evidence|source|citation)\b/i },
  { role: "scholar", weight: 2, re: /\b(codex|gate \d+|47 gates|concept)\b/i },

  // CREATOR — making things.
  { role: "creator", weight: 4, re: /\b(write|lyrics?|verse|bar[s]?|hook|song|poem|caption|script|album concept|artwork|visual direction|name (this|my)|title ideas?)\b/i },

  // STRATEGIST — business, growth, systems.
  { role: "strategist", weight: 4, re: /\b(strategy|strategic|revenue|pricing|price|monetis|monetiz|launch|release plan|marketing|audience|funnel|conversion|roadmap|business|seo|analytics|growth)\b/i },

  // GUIDE — orientation and navigation.
  { role: "guide", weight: 4, re: /\b(where (do|should) i (start|begin)|what should i (read|listen|do|start with)|how do i (get started|navigate)|recommend|next step|new here|show me around)\b/i },
  { role: "guide", weight: 2, re: /\b(who is buddha ka|what is kafrequency|what is this (site|website)|how much|buy|purchase|download|membership)\b/i },
];

const VALID: SpecialistRole[] = ["guide", "scholar", "creator", "strategist", "deep_temple"];

export function isSpecialistRole(v: unknown): v is SpecialistRole {
  return typeof v === "string" && (VALID as string[]).includes(v);
}

/**
 * Choose the specialist for a turn.
 * @param text the latest user message
 * @param override explicit user selection ("auto" or a role)
 */
export function routeSpecialist(text: string, override?: string | null): RouteDecision {
  if (override && override !== "auto" && isSpecialistRole(override)) {
    return { role: override, reason: "chosen by the visitor", forced: true };
  }
  const q = (text ?? "").trim();
  if (!q) return { role: "guide", reason: "no question yet", forced: false };

  const scores = new Map<SpecialistRole, number>();
  for (const s of SIGNALS) {
    if (s.re.test(q)) scores.set(s.role, (scores.get(s.role) ?? 0) + s.weight);
  }

  // Long, layered questions lean deeper.
  if (q.length > 420 || (q.match(/\?/g)?.length ?? 0) >= 3) {
    scores.set("deep_temple", (scores.get("deep_temple") ?? 0) + 2);
  }

  let best: SpecialistRole = "scholar";
  let bestScore = 0;
  for (const role of VALID) {
    const s = scores.get(role) ?? 0;
    if (s > bestScore) {
      best = role;
      bestScore = s;
    }
  }
  if (bestScore === 0) {
    // Short factual-looking question → guide; otherwise reason it through.
    const role: SpecialistRole = q.length < 60 ? "guide" : "scholar";
    return { role, reason: "no strong signal", forced: false };
  }
  return { role: best, reason: `matched ${best} signals (${bestScore})`, forced: false };
}

export const ROLE_LABELS: Record<SpecialistRole, string> = {
  guide: "Guide",
  scholar: "Scholar",
  creator: "Creator",
  strategist: "Strategist",
  deep_temple: "Deep Temple",
};
